// SendDataPanelV3 — collapsible "send data here" surface.
//
// Vision: operator should never wonder "where do I send my data" — the
// webhook URL, drop zone, and a working curl snippet are all visible
// without leaving the page. "Send test" fires a sample event so the
// operator sees the full pipeline (ingest → preview → BQ view) work
// end-to-end with one click.

import React, { useRef, useState } from 'react';
import type { Source } from '@datahub/shared';
import { useToast } from '../Toast';
import { HooksPanelV3 } from './HooksPanelV3';

export function SendDataPanelV3({ source, onTestSent, onSourceChange }: {
  source: Source;
  onTestSent: () => void;
  onSourceChange?: () => void;
}): React.ReactElement {
  const toast = useToast();
  // Default collapsed when the source already has events — at that
  // point the operator's loop is explore→model, not send-more. We
  // remember the toggle per session so once they expand it (e.g. to
  // grab the curl snippet) it stays open until they collapse it.
  // First-visit / brand-new source still auto-opens because the
  // empty-state hero handles the empty path separately.
  const storageKey = `v3.sendpanel.opened.${source.source_id}`;
  const [open, setOpen] = useState(() => {
    try {
      const stored = sessionStorage.getItem(storageKey);
      if (stored === '1') return true;
      if (stored === '0') return false;
      return false; // default collapsed for steady-state operator
    } catch { return false; }
  });
  function setOpenPersist(next: boolean): void {
    setOpen(next);
    try { sessionStorage.setItem(storageKey, next ? '1' : '0'); } catch { /* private mode */ }
  }
  const [snippet, setSnippet] = useState<'curl' | 'node' | 'python'>('curl');
  const [sending, setSending] = useState(false);
  // Custom-payload editor: when expanded, operator can paste arbitrary
  // JSON to send instead of the default `{event: 'test.from_ui', …}`
  // payload. Keeps the default behaviour for one-click testing while
  // letting power users iterate on shape without leaving the page.
  const [showCustom, setShowCustom] = useState(false);
  const [customPayload, setCustomPayload] = useState('{\n  "event": "order.created",\n  "order_id": "ORD-1",\n  "amount": 99.50\n}');
  const [customError, setCustomError] = useState<string | null>(null);

  const webhookInput = source.inputs.find(i => i.kind === 'webhook');
  const webhookUrl = webhookInput?.url ?? '';

  // File upload via Browse button — explicit alternative to the
  // window-level drag-and-drop registered by SourceDetailV3Page.
  // Operators on tablets / kiosk setups can't drag, and the prior
  // "drop anywhere" hint was easy to miss. The button is wired to a
  // hidden file input; selecting a file POSTs to /upload/<id>.
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  async function uploadPickedFile(file: File): Promise<void> {
    setUploading(true);
    try {
      const r = await fetch(`/upload/${source.source_id}?bulk=true&filename=${encodeURIComponent(file.name)}`, {
        method: 'POST',
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        body: file,
      });
      if (r.ok || r.status === 202) {
        toast.show(`Uploaded ${file.name} — refreshing data`, { kind: 'success' });
        onTestSent();
      } else {
        const body = await r.json().catch(() => ({})) as { error?: { code?: string; detail?: string } };
        toast.show(`Upload failed: ${body.error?.detail ?? r.status}`, { kind: 'error' });
      }
    } catch (err) {
      toast.show(`Upload failed: ${(err as Error).message}`, { kind: 'error' });
    } finally {
      setUploading(false);
      // Reset the input so picking the same file again re-fires change.
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }
  // Email input is intentionally NOT surfaced — the inbound MX
  // handler isn't wired (BACKLOG: Email ingest), the placeholder
  // address confused operators. Re-enable this destructure when MX
  // ships.
  const fileInput = source.inputs.find(i => i.kind === 'file_upload');

  function copy(text: string, label: string): void {
    void navigator.clipboard.writeText(text);
    toast.show(`${label} copied`, { kind: 'success' });
  }

  async function sendTest(useCustom: boolean): Promise<void> {
    if (!webhookUrl) return;
    let body: string;
    if (useCustom) {
      try {
        // Round-trip via JSON.parse → JSON.stringify so the body is
        // canonical (no trailing whitespace, syntax errors caught
        // before the request fires).
        const parsed = JSON.parse(customPayload) as unknown;
        body = JSON.stringify(parsed);
        setCustomError(null);
      } catch (e) {
        setCustomError(`Invalid JSON: ${(e as Error).message.slice(0, 80)}`);
        return;
      }
    } else {
      body = JSON.stringify({
        event: 'test.from_ui',
        message: 'Sent from the Send Data panel',
        ts: new Date().toISOString(),
      });
    }
    setSending(true);
    try {
      const r = await fetch(webhookUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
      });
      if (r.ok || r.status === 202) {
        toast.show('Test event sent — refreshing data table', { kind: 'success' });
        onTestSent();
      } else {
        toast.show(`Test send failed (${r.status})`, { kind: 'error' });
      }
    } catch (e) {
      toast.show(`Test send failed: ${(e as Error).message}`, { kind: 'error' });
    } finally {
      setSending(false);
    }
  }

  const curlSnippet = `curl -X POST '${webhookUrl}' \\
  -H 'Content-Type: application/json' \\
  -d '{"event":"hello","value":42}'`;
  const nodeSnippet = `await fetch('${webhookUrl}', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({ event: 'hello', value: 42 }),
});`;
  const pythonSnippet = `import requests
requests.post('${webhookUrl}', json={'event': 'hello', 'value': 42})`;

  const snippetText = snippet === 'curl' ? curlSnippet : snippet === 'node' ? nodeSnippet : pythonSnippet;

  return (
    <div style={{ marginBottom: 18, border: '1px solid var(--line)', borderRadius: 8, background: 'var(--bg-elev)', overflow: 'hidden' }}>
      <button
        type="button"
        onClick={() => setOpenPersist(!open)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 14px',
          background: 'transparent', border: 'none', cursor: 'pointer',
          color: 'var(--ink)', fontFamily: 'var(--ui)', fontSize: 13,
        }}
      >
        <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--ink-3)' }}>{open ? '▾' : '▸'}</span>
        <strong>Send data to this source</strong>
        <span style={{ flex: 1 }} />
        {!open && webhookUrl && (
          <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--ink-3)' }}>{webhookUrl}</span>
        )}
      </button>

      {open && (
        <div style={{ padding: '0 14px 14px 14px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Inputs list — multi-kind (webhook + file_upload + api_pull
              + email + manual). Each can be named, tagged with key=value
              pairs, and produces events stamped with input_id /
              input_name / input_tags so views can filter per input. */}
          <div>
            <label style={{ fontSize: 10.5, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)', display: 'block', marginBottom: 4 }}>Inputs</label>
            <HooksPanelV3 source={source} onChange={() => onSourceChange?.()} />
            {webhookUrl && (
              <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                <button
                  className="btn sm primary"
                  onClick={() => { void sendTest(false); }}
                  disabled={sending}
                  style={{ padding: '4px 14px' }}
                  title="Sends a sample JSON event to the default webhook for this source"
                >{sending ? 'Sending…' : '🧪 Send test event'}</button>
                <button
                  className="btn sm ghost"
                  onClick={() => setShowCustom(s => !s)}
                  style={{ padding: '4px 10px', fontSize: 11 }}
                  title="Send a custom JSON payload"
                >{showCustom ? 'hide custom' : '+ custom payload'}</button>
              </div>
            )}
            {showCustom && webhookUrl && (
              <div style={{ marginTop: 8 }}>
                <textarea
                  value={customPayload}
                  onChange={e => { setCustomPayload(e.target.value); setCustomError(null); }}
                  spellCheck={false}
                  placeholder='{"event":"order.created","order_id":"ORD-1"}'
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    minHeight: 110, padding: '8px 10px',
                    fontFamily: 'var(--mono)', fontSize: 12, lineHeight: 1.5,
                    border: '1px solid var(--line)', borderRadius: 4,
                    background: 'var(--bg)', color: 'var(--ink)',
                    resize: 'vertical',
                  }}
                />
                {customError && <div style={{ marginTop: 4, fontSize: 11, color: 'var(--danger)' }}>{customError}</div>}
                <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                  <button
                    className="btn sm primary"
                    onClick={() => { void sendTest(true); }}
                    disabled={sending}
                    style={{ padding: '4px 14px' }}
                  >{sending ? 'Sending…' : 'Send custom payload'}</button>
                  <span style={{ fontSize: 11, color: 'var(--ink-3)', alignSelf: 'center' }}>
                    POSTs to the default webhook for this source.
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Code snippets */}
          {webhookUrl && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                <label style={{ fontSize: 10.5, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)' }}>Code</label>
                <span style={{ flex: 1 }} />
                {(['curl', 'node', 'python'] as const).map(s => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setSnippet(s)}
                    style={{
                      padding: '2px 8px', fontSize: 11, fontFamily: 'var(--mono)',
                      background: snippet === s ? 'var(--accent-soft)' : 'transparent',
                      border: `1px solid ${snippet === s ? 'var(--accent-line)' : 'var(--line)'}`,
                      color: snippet === s ? 'var(--accent)' : 'var(--ink-3)',
                      borderRadius: 3, cursor: 'pointer',
                    }}
                  >{s}</button>
                ))}
                <button
                  type="button"
                  onClick={() => copy(snippetText, snippet)}
                  className="btn sm ghost"
                  style={{ padding: '2px 8px', fontSize: 11 }}
                >copy</button>
              </div>
              <pre style={{
                margin: 0, padding: '10px 12px',
                background: 'var(--bg-sunken)', border: '1px solid var(--line)', borderRadius: 4,
                fontFamily: 'var(--mono)', fontSize: 11.5, color: 'var(--ink-2)',
                whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              }}>{snippetText}</pre>
            </div>
          )}

          {/* File upload at-a-glance — only surface if it's actually
              wired (no placeholder cards). Email + api_pull are
              hidden in V3 until inbound MX + scheduled-pull config
              UI ship; the bare placeholder addresses confused
              operators. */}
          {/* File upload row — always rendered (the window-level drop
              overlay registered by SourceDetailV3Page works regardless of
              whether the source has a file_upload input declared). Product
              audit #16 flagged the inconsistent affordance: drop worked
              but the hint only appeared when fileInput existed. */}
          <div style={{ padding: '10px 12px', background: 'var(--bg-sunken)', border: '1px solid var(--line)', borderRadius: 4, fontSize: 12, color: 'var(--ink-2)' }}>
              <div style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>File upload</div>
              <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="btn sm primary"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  style={{ padding: '4px 14px' }}
                  title="Pick a file from your device. Supports CSV · TSV · JSON · NDJSON · XLS · XLSX · XML (UBL / SEPA)."
                >📂 {uploading ? 'Uploading…' : 'Browse file…'}</button>
                <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                  or drop a file anywhere on this page · CSV · JSON · XLSX · XML
                </span>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.tsv,.json,.ndjson,.xls,.xlsx,.xml,.zip,.gz,application/json,text/csv,text/plain,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,application/xml,text/xml"
                  hidden
                  onChange={e => {
                    const f = e.target.files?.[0];
                    if (f) { void uploadPickedFile(f); }
                  }}
                />
              </div>
              {fileInput?.url && (
                <div style={{ marginTop: 6, fontFamily: 'var(--mono)', fontSize: 11 }}>
                  API endpoint: <code>{fileInput.url}</code>
                </div>
              )}
            </div>
        </div>
      )}
    </div>
  );
}
